import React from 'react';
import { INavLinkGroup } from '@fluentui/react/lib/Nav';
import TimeTaxSupportSystemClient from '../../system/TimeTaxSupportSystemClient';
import { IMenuGroup } from '../../system/menuPermision/MenuPermisionSystem';

interface IMenuPermissionContextValue {
  menu: INavLinkGroup[];
  menuUrls: string[] | null;
  loading: boolean;
}

const MenuPermissionContext = React.createContext<IMenuPermissionContextValue>({
  menu: [],
  menuUrls: null,
  loading: false,
});

interface IMenuPermissionProviderProps {
  system: TimeTaxSupportSystemClient;
  userId?: string | null;
  children?: React.ReactNode;
}

const collectMenuUrls = (groups: IMenuGroup[]): string[] => {
  const urls: string[] = [];

  const walkLinks = (links: IMenuGroup['links']) => {
    links.forEach((link) => {
      if (link.url) urls.push(link.url);
      if (link.links && link.links.length > 0) walkLinks(link.links);
    });
  };

  groups.forEach((group) => {
    if (group.links && group.links.length > 0) walkLinks(group.links);
  });

  return urls;
};

export const normalizePath = (path: string) => {
  const normalized = `/${path.replace(/^\/+/, '')}`;
  return normalized.length > 1 && normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
};

export const isRouteAllowed = (menuUrls: string[], pathname: string) => {
  const current = normalizePath(pathname);

  return menuUrls.some((menuUrl) => {
    const url = normalizePath(menuUrl);
    if (url === '/') return false;
    return current === url || current.startsWith(`${url}/`);
  });
};

export const MenuPermissionProvider = ({ system, userId, children }: IMenuPermissionProviderProps) => {
  const [menu, setMenu] = React.useState<INavLinkGroup[]>([]);
  const [menuUrls, setMenuUrls] = React.useState<string[] | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!userId) {
      setMenu([]);
      setMenuUrls(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchMenu = async () => {
      try {
        const data = await system.getMenuPermissionSystem().getMenuService();
        if (cancelled) return;
        const groups = data || [];
        setMenu(groups as unknown as INavLinkGroup[]);
        setMenuUrls(collectMenuUrls(groups));
      } catch (error) {
        console.log(error);
        if (!cancelled) {
          setMenu([]);
          setMenuUrls(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMenu();

    return () => {
      cancelled = true;
    };
  }, [system, userId]);

  const value = React.useMemo(() => ({ menu, menuUrls, loading }), [menu, menuUrls, loading]);

  return <MenuPermissionContext.Provider value={value}>{children}</MenuPermissionContext.Provider>;
};

export const useMenuPermission = () => React.useContext(MenuPermissionContext);
